// src/components/Main/MainScreen.jsx

import React, { useState, useContext } from 'react';
import { DataContext } from '../../DataContext'; // Adjust the path as needed
import { auth, firestore } from '../../firebase';
import { deleteDoc, doc } from 'firebase/firestore';
import MemberTable from '../MemberTable/MemberTable';
import MemberForm from '../Forms/MemberForm';
import MessageSender from '../Communications/MessageSender';
import Settings from '../Settings/Settings';
import Insights from '../Insights/Insights';
import Users from '../Users/Users';
import CustomModal from '../Common/CustomModal';
import ToastContainer from '../Common/ToastContainer';
import Header from './Header';
import Footer from './Footer';
import DeleteConfirmationModal from '../Common/DeleteConfirmationModal';
import MainContent from './MainContent';
import { useColorSettings } from '../Common/ColorSettingsContext';
import { useLanguageSettings } from '../Common/LanguageSettingsContext';
import { useTranslation } from 'react-i18next';
import './MainScreen.css';
import { useQueryClient } from '@tanstack/react-query';

const MainScreen = () => {
  const { t } = useTranslation();
  const { appSettings } = useColorSettings();
  const {
    sortedRecords, // Use sortedRecords from DataContext
    userRole,
    userData,
    currentPage,
    totalPages,
    pageNumbers,
    onPrev,
    onNext,
    onPageClick,
    itemsPerPage,
    onItemsPerPageChange,
    sortField,
    sortOrder,
    setSorting,
  } = useContext(DataContext);

  const queryClient = useQueryClient();

  const [isTransitioning, setIsTransitioning] = useState(false);
  const [currentTab, setCurrentTab] = useState('members');
  const [nextTab, setNextTab] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [modalTitle, setModalTitle] = useState('');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');

  const [toasts, setToasts] = useState([]);

  const showToastMessage = (message, type = 'success', duration = 5000) => {
    const id = Date.now();
    setToasts((prevToasts) => [...prevToasts, { id, message, type, duration }]);
  };

  const removeToast = (id) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
      showToastMessage(t('mainScreen.logoutError'), 'error');
    }
  };

  const handleResetPassword = async () => {
    try {
      const email = auth.currentUser.email;
      if (email) {
        await auth.sendPasswordResetEmail(email);
        showToastMessage(t('mainScreen.resetEmailSent'), 'success');
      } else {
        showToastMessage(t('mainScreen.noEmailAssociated'), 'error');
      }
    } catch (error) {
      console.error('Error sending password reset email:', error);
      showToastMessage(t('mainScreen.resetEmailError'), 'error');
    }
  };

  const handleTabChange = (tab) => {
    if (tab === currentTab || isTransitioning) return;
    setNextTab(tab);
    setIsTransitioning(true);
  };

  React.useEffect(() => {
    if (isTransitioning && nextTab) {
      const timeout = setTimeout(() => {
        setCurrentTab(nextTab);
        setNextTab(null);
        setIsTransitioning(false);
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [isTransitioning, nextTab]);

  const handleOpenModal = (content, title) => {
    setModalContent(content);
    setModalTitle(title);
    setShowModal(true);
  };

  const sleep = (ms) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  const handleCloseModal = async () => {
    setShowModal(false);
    await sleep(300);
    setModalContent(null);
    setModalTitle('');
  };

  const handleAddMember = () => {
    handleOpenModal(
      <MemberForm onClose={handleCloseModal} onUpdate={handleUpdate} />,
      t('mainScreen.newMember')
    );
  };

  const handleEdit = (item) => {
    handleOpenModal(
      <MemberForm
        initialData={item}
        onClose={handleCloseModal}
        onUpdate={handleUpdate}
      />,
      `${item.nombre} ${item.apellido}`
    );
  };

  const handleDelete = (item) => {
    setRecordToDelete(item);
    setShowDeleteModal(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setRecordToDelete(null);
  };

  const confirmDelete = async () => {
    if (!recordToDelete) return;

    try {
      await deleteDoc(doc(firestore, 'records', recordToDelete.id));
      showToastMessage(
        t('mainScreen.recordDeleted', { name: `${recordToDelete.nombre} ${recordToDelete.apellido}` }),
        'success'
      );
      queryClient.invalidateQueries(['allRecords']); // Refetch allRecords after deletion
    } catch (error) {
      console.error('Error deleting record:', error);
      showToastMessage(t('mainScreen.deleteRecordError'), 'error');
    } finally {
      setShowDeleteModal(false);
      setRecordToDelete(null);
    }
  };

  const handleUpdate = async (updatedItem) => {
    try {
      // Update the record in Firestore
      await firestore.collection('records').doc(updatedItem.id).set(updatedItem, { merge: true });
      showToastMessage(t('mainScreen.recordUpdated'), 'success');
      queryClient.invalidateQueries(['allRecords']); // Refetch allRecords after update
    } catch (error) {
      console.error('Error updating record:', error);
      showToastMessage(t('mainScreen.recordUpdateError'), 'error');
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSort = (field) => {
    setSorting(field);
    // `setSorting` already manages sortField and sortOrder in DataContext
  };

  const handleSendMessage = (member) => {
    if (!member.telefono) {
      showToastMessage(t('mainScreen.phoneRequired'), 'error');
      return;
    }
    handleOpenModal(
      <MessageSender
        single={true}
        recipient={member.telefono}
        onClose={handleCloseModal}
        showToast={showToastMessage}
      />,
      t('mainScreen.sendMessageTo', { name: member.nombre, surname: member.apellido })
    );
  };

  // **Added Export Functions Start Here**

  // Helper function to capitalize strings
  const capitalize = (str) => {
    if (!str) return '';
    return str
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper function to remove special characters
  const removeSpecialCharacters = (str) => {
    if (!str) return '';
    return str.replace(/[^a-zA-Z0-9\s]/g, '');
  };

  // Function to clean individual records
  const cleanRecord = (record) => {
    // Clone the record to avoid mutating the original
    let cleaned = { ...record };

    // Capitalize 'nombre' and 'apellido'
    cleaned.nombre = capitalize(cleaned.nombre);
    cleaned.apellido = capitalize(cleaned.apellido);

    // Parse 'sacramentos' field into an array safely
    const sacramentosArray = cleaned.sacramentos

    // Map specific sacraments to boolean fields based on substrings
    cleaned.baptism = sacramentosArray.some(sacramento => sacramento.includes('Bautismo'));
    cleaned.firstCommunion = sacramentosArray.some(sacramento => sacramento.includes('Primera'));
    cleaned.confirmation = sacramentosArray.some(sacramento => sacramento.includes('Confirma'));

    // Remove the original 'sacramentos' field
    delete cleaned.sacramentos;

    // Remove special characters from all string fields
    Object.keys(cleaned).forEach((key) => {
      if (typeof cleaned[key] === 'string') {
        cleaned[key] = removeSpecialCharacters(cleaned[key]);
      }
    });

    return cleaned;
  };

  // Function to handle export format selection
  const handleExport = (format) => {
    if (format === 'csv') {
      exportToCSV();
    } else if (format === 'json') {
      exportToJSON();
    }
  };

  // Function to export data as JSON
  const exportToJSON = () => {
    if (!sortedRecords || sortedRecords.length === 0) {
      showToastMessage(t('mainScreen.noRecordsToExport'), 'error');
      return;
    }

    // Clean all records before exporting
    const cleanedData = sortedRecords.map(cleanRecord);

    const dataStr = JSON.stringify(cleanedData, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = 'members.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url); // Clean up
  };

  // Function to get all unique headers from records
  const getAllHeaders = (records) => {
    const headersSet = new Set();
    records.forEach((record) => {
      Object.keys(record).forEach((key) => headersSet.add(key));
    });
    return Array.from(headersSet);
  };

  // Function to export data as CSV
  const exportToCSV = () => {
    if (!sortedRecords || sortedRecords.length === 0) {
      showToastMessage(t('mainScreen.noRecordsToExport'), 'error');
      return;
    }

    console.log(sortedRecords);
    // Clean all records before exporting
    const cleanedData = sortedRecords.map(cleanRecord);
    
    const headers = getAllHeaders(cleanedData);

    const csvRows = [];
    // Add headers
    csvRows.push(headers.join(','));

    // Add data
    cleanedData.forEach((record) => {
      const values = headers.map((header) => {
        let value = record[header];
        if (typeof value === 'boolean') {
          value = value ? 'true' : 'false';
        } else if (value === undefined || value === null) {
          value = '';
        }
        const escaped = ('' + value).replace(/"/g, '""').replace(/\n/g, '\\n');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(','));
    });

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = 'members.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url); // Clean up
  };

  // **Added Export Functions End Here**

  return (
    <div className="main-screen-app">
      <Header
        userData={userData}
        onLogout={handleLogout}
        onResetPassword={handleResetPassword}
        logoUrl={appSettings.logoUrl}
        currentTab={currentTab}
        onTabChange={handleTabChange}
        userRole={userRole}
        isTransitioning={isTransitioning}
      />

      <MainContent
        currentTab={currentTab}
        isTransitioning={isTransitioning}
        components={{
          members: (
            <MemberTable
              onSort={handleSort}
              sortField={sortField}
              sortOrder={sortOrder}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onAddMember={handleAddMember}
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              userRole={userRole}
              onSendMessage={handleSendMessage}
              onExport={handleExport} // Passed down the handleExport function
            />
          ),
          communications: <MessageSender showToast={showToastMessage} onClose={handleCloseModal} />,
          settings: <Settings showToast={showToastMessage} />,
          users: userRole === 'admin' && <Users showToast={showToastMessage} />,
          insights: userRole === 'admin' && <Insights />,
        }}
      />

      <CustomModal show={showModal} onClose={handleCloseModal} title={modalTitle}>
        {modalContent}
      </CustomModal>

      {showDeleteModal && (
        <DeleteConfirmationModal
          show={showDeleteModal}
          onConfirm={confirmDelete}
          onCancel={handleCancelDelete}
          record={recordToDelete}
        />
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <Footer />
    </div>
  );
};

export default MainScreen;
